import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { InvestmentService } from '../investment.service';
import { AmortisationModel } from './amortisation.model';
import { Utils } from '../../../shared/utils';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { LoadingDialogService } from '../../../shared/ui/loading/loading-dialog.service';

export interface AmortisationTableDialogData {
  uid: string;
  name?: string;
  year?: number;
}

interface AmortisationYearGroup {
  year: number;
  total: number;
  lines: AmortisationModel[];
}

@Component({
    selector: 'convfact-amortisationtable',
    templateUrl: './table-dialog.component.html',
    styleUrls: ['./table-dialog.component.scss'],
    standalone: false
})
export class AmortisationTableDialogComponent implements OnInit {
  ready = false;
  loadError = false;

  name = '';
  account = '';
  acumulated = 0;
  rest = 0;
  total = 0;
  groups: AmortisationYearGroup[] = [];

  static open(dialog: MatDialog, data: AmortisationTableDialogData): Observable<void> {
    return dialog.open<AmortisationTableDialogComponent, AmortisationTableDialogData>(AmortisationTableDialogComponent, {
      width: '720px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(() => undefined));
  }

  constructor(
    public dialogRef: MatDialogRef<AmortisationTableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AmortisationTableDialogData,
    private investmentService: InvestmentService,
    private notify: NotificationService,
    private loading: LoadingDialogService,
  ) { }

  ngOnInit(): void {
    this.name = this.data.name || '';
    this.load();
  }

  load(): void {
    this.ready = false;
    this.loadError = false;
    this.investmentService.GetAmortisation(this.data.uid)
      .then(response => {
        const lines = Array.isArray(response) ? response : [];
        this.acumulated = Utils.RoundTwoDecimals(lines.reduce((acc, line) => acc + (Number(line.amount) || 0), 0));
        const last = lines.length ? lines[lines.length - 1] : null;
        this.total = Utils.RoundTwoDecimals(last ? Number(last.total) || 0 : 0);
        this.rest = Utils.RoundTwoDecimals(this.total - this.acumulated);
        if (last) {
          this.name = last.name || this.name;
          this.account = last.accountActivity || '';
        }
        this.groups = this.group(lines);
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  export(): void {
    this.loading.run('Generando el cuadro de amortización…', this.investmentService.export(this.data.uid))
      .then(response => {
        FileSaver.saveAs(response, Utils.GetExcelName('Amortizacion_' + (this.name || 'inversion')));
        this.notify.ok('Cuadro de amortización exportado');
      })
      .catch(() => this.notify.error('No se pudo exportar el cuadro de amortización'));
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  isCurrent(group: AmortisationYearGroup): boolean {
    return !!this.data.year && Number(this.data.year) === group.year;
  }

  trackByYear(_: number, group: AmortisationYearGroup): number {
    return group.year;
  }

  private group(lines: AmortisationModel[]): AmortisationYearGroup[] {
    const groups: AmortisationYearGroup[] = [];
    lines.forEach(line => {
      let group = groups.find(item => item.year === line.year);
      if (!group) {
        group = { year: line.year, total: 0, lines: [] };
        groups.push(group);
      }
      group.lines.push(line);
      group.total = Utils.RoundTwoDecimals(group.total + (Number(line.amount) || 0));
    });
    return groups;
  }
}
